import { useMemo } from 'react';
import type { ComponentType } from 'react';
import { generatedDocs } from './docsRegistry.generated';
import { useDocsVisibility } from './docsVisibility';

export type DocFrontmatter = {
  id: string;
  title: string;
  owner: string;
  status: string;
  lastReviewed: string;
  visibility: 'Confidential' | 'internal' | 'public';
  tags: string[];
};

export type DocEntry = DocFrontmatter & {
  Content: ComponentType;
  sourcePath: string;
  categoryPath: string;
  changelog: string[];
};

export type DocCategory = {
  id: string;
  title: string;
  rootDoc?: DocEntry;
  docs: DocEntry[];
};

const DOCS = generatedDocs.map((entry) => ({
  ...(entry.frontmatter as DocFrontmatter),
  Content: entry.Content,
  sourcePath: entry.sourcePath,
  categoryPath: entry.categoryPath,
  changelog: entry.changelog ?? [],
}));

const PROD_PATH_ALLOWLIST = ['libs', 'apps'];
const PROD_TAG_ALLOWLIST = ['feature', 'features'];

const isProd = process.env.NODE_ENV === 'production';

const passesProdFilter = (doc: DocEntry) => {
  const isAllowedPath = PROD_PATH_ALLOWLIST.some((prefix) =>
    doc.sourcePath.startsWith(prefix),
  );
  const hasAllowedTag = doc.tags?.some((tag) =>
    PROD_TAG_ALLOWLIST.includes(tag.toLowerCase()),
  );

  return isAllowedPath || hasAllowedTag;
};

export const useDocs = () => {
  const { showAllDocs } = useDocsVisibility();

  return useMemo(() => {
    if (!isProd && showAllDocs) {
      return DOCS;
    }

    return DOCS.filter(passesProdFilter);
  }, [showAllDocs]);
};

export const useDocById = (docId?: string) => {
  const docs = useDocs();
  return useMemo(() => docs.find((entry) => entry.id === docId), [docs, docId]);
};

export const useDocsGrouped = () => {
  const docs = useDocs();

  return useMemo(() => {
    const grouped = new Map<string, DocEntry[]>();
    docs.forEach((doc) => {
      const key = doc.categoryPath;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(doc);
    });

    const sorted = Array.from(grouped.entries())
      .map(([key, entries]): [string, DocEntry[]] => [
        key,
        entries.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')),
      ])
      .sort(([a], [b]) => a.localeCompare(b));

    return sorted;
  }, [docs]);
};

const getCategoryId = (doc: DocEntry) => doc.categoryPath.split('/')[0] ?? '';

export const useDocCategories = () => {
  const docs = useDocs();

  return useMemo(() => {
    const grouped = new Map<string, DocEntry[]>();
    docs.forEach((doc) => {
      const key = getCategoryId(doc);
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(doc);
    });

    return Array.from(grouped.entries())
      .map(([id, entries]) => {
        const rootDoc = entries.find((doc) => doc.categoryPath === id);
        const title = rootDoc?.title ?? id;
        return {
          id,
          title,
          rootDoc,
          docs: entries.sort((a, b) =>
            (a.title ?? '').localeCompare(b.title ?? ''),
          ),
        };
      })
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [docs]);
};

export const useCategoryById = (categoryId?: string) => {
  const categories = useDocCategories();
  return useMemo(
    () => categories.find((category) => category.id === categoryId),
    [categories, categoryId],
  );
};
